import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { buildRecommendations, computeHealthProfileCompleteness, deriveExperience } from './recommendationEngine';
import { useAuth } from '../components/AuthContext';
import { useLanguage } from '../i18n/LanguageContext';
import {
  createConsent,
  createFeedback,
  createObservation,
  fetchCareContext,
  recordRecommendationAction as persistRecommendationAction,
  revokeConsent as persistConsentRevoke,
  saveHealthProfile
} from './api';

const STORAGE_KEY = 'kmq-care-coordinator-v1';
const CareCoordinatorContext = createContext(null);

function defaultProfileFromUser(user = {}) {
  return {
    legalName: [user.first_name, user.last_name].filter(Boolean).join(' ').trim() || user.name || '',
    dateOfBirth: user.birth_date || '',
    phone: user.phone || '',
    address: user.address || '',
    postalCode: user.postal_code || '',
    emergencyContactName: user.emergency_contact || '',
    emergencyContactPhone: '',
    conditions: [],
    medications: [],
    allergies: [],
    diagnoses: [],
    riskFactors: [],
    symptoms: [],
    mobilityStatus: '',
    behaviorObservations: '',
    cognitiveNotes: '',
    carePreferences: '',
    preferredLanguage: user.preferred_language || '',
    culturalPreferences: '',
    devicePlaceholders: '',
    careTeam: [],
    consentStatus: 'self',
    delegationPermissions: [],
    profileReminderDismissed: false
  };
}

function emptyStore() {
  return {
    subjects: {},
    observations: [],
    consents: [],
    auditLog: [],
    feedback: [],
    recommendationActions: []
  };
}

function parseStorage() {
  if (typeof window === 'undefined') return emptyStore();
  try {
    return { ...emptyStore(), ...(JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '{}')) };
  } catch {
    return emptyStore();
  }
}

function persistStorage(store) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
}

function upsertSubject(store, user) {
  if (!user?.user_id) return store;
  const existing = store.subjects[user.user_id] || {};
  return {
    ...store,
    subjects: {
      ...store.subjects,
      [user.user_id]: {
        id: user.user_id,
        ownerId: user.user_id,
        displayName: [user.first_name, user.last_name].filter(Boolean).join(' ').trim() || user.name || user.email || 'Beneficiary',
        preferredLanguage: existing.preferredLanguage || user.preferred_language || '',
        relationshipLabel: existing.relationshipLabel || 'self',
        healthProfile: {
          ...defaultProfileFromUser(user),
          ...(existing.healthProfile || {})
        },
        createdAt: existing.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }
    }
  };
}

function buildManagedSubjects(user, store) {
  if (!user?.user_id) return [];
  const ownSubject = store.subjects[user.user_id] || {
    id: user.user_id,
    ownerId: user.user_id,
    displayName: [user.first_name, user.last_name].filter(Boolean).join(' ').trim() || user.name || user.email || 'Beneficiary',
    preferredLanguage: user.preferred_language || '',
    relationshipLabel: 'self',
    healthProfile: defaultProfileFromUser(user)
  };

  const delegateMatches = store.consents
    .filter((entry) => entry.status === 'active' && (entry.delegateUserId === user.user_id || entry.delegateEmail === user.email))
    .map((entry) => {
      const subject = store.subjects[entry.beneficiaryId];
      if (!subject) return null;
      return {
        ...subject,
        relationshipLabel: entry.relationship || 'delegate',
        consentId: entry.id,
        delegatePermissions: entry.permissions || []
      };
    })
    .filter(Boolean);

  const seen = new Set();
  return [ownSubject, ...delegateMatches].filter((subject) => {
    if (seen.has(subject.id)) return false;
    seen.add(subject.id);
    return true;
  });
}

function mergeBackendContext(currentStore, context, fallbackUser) {
  const subjects = Object.fromEntries((context.managedSubjects || []).map((subject) => ([
    subject.id,
    {
      ...subject,
      healthProfile: {
        ...defaultProfileFromUser(fallbackUser),
        ...(subject.healthProfile || {})
      }
    }
  ])));

  return {
    ...currentStore,
    subjects: {
      ...currentStore.subjects,
      ...subjects
    },
    observations: context.observations || currentStore.observations,
    consents: context.consents || currentStore.consents,
    auditLog: context.auditLog || currentStore.auditLog,
    feedback: context.feedback || currentStore.feedback,
    recommendationActions: context.recommendationActions || currentStore.recommendationActions
  };
}

export function CareCoordinatorProvider({ children }) {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [store, setStore] = useState(parseStorage);
  const [activeSubjectId, setActiveSubjectId] = useState(null);
  const [backendAvailable, setBackendAvailable] = useState(false);

  useEffect(() => {
    if (!user?.user_id) return;
    setStore((current) => {
      const next = upsertSubject(current, user);
      persistStorage(next);
      return next;
    });
  }, [user]);

  useEffect(() => {
    persistStorage(store);
  }, [store]);

  useEffect(() => {
    if (!user?.user_id) return;
    let active = true;

    fetchCareContext(user.user_id, user.email)
      .then((context) => {
        if (!active) return;
        setStore((current) => mergeBackendContext(current, context, user));
        setBackendAvailable(true);
      })
      .catch(() => {
        if (active) setBackendAvailable(false);
      });

    return () => {
      active = false;
    };
  }, [user]);

  const managedSubjects = useMemo(() => buildManagedSubjects(user, store), [store, user]);

  useEffect(() => {
    if (!managedSubjects.length) {
      setActiveSubjectId(null);
      return;
    }

    if (!activeSubjectId || !managedSubjects.some((subject) => subject.id === activeSubjectId)) {
      setActiveSubjectId(managedSubjects[0].id);
    }
  }, [activeSubjectId, managedSubjects]);

  const activeSubject = useMemo(
    () => managedSubjects.find((subject) => subject.id === activeSubjectId) || managedSubjects[0] || null,
    [activeSubjectId, managedSubjects]
  );

  const recommendations = useMemo(
    () => buildRecommendations({
      user,
      activeSubject,
      managedSubjects,
      observations: store.observations,
      feedback: store.feedback,
      language: language === 'en' ? 'en' : 'fr'
    }),
    [activeSubject, language, managedSubjects, store.feedback, store.observations, user]
  );

  const value = useMemo(() => {
    const logAudit = (entry) => {
      setStore((current) => ({
        ...current,
        auditLog: [
          {
            id: crypto.randomUUID(),
            at: new Date().toISOString(),
            actorId: user?.user_id || null,
            actorEmail: user?.email || null,
            subjectId: activeSubject?.id || user?.user_id || null,
            ...entry
          },
          ...current.auditLog
        ].slice(0, 200)
      }));
    };

    const updateHealthProfile = async (subjectId, updater) => {
      const existingSubject = store.subjects[subjectId];
      const currentProfile = existingSubject?.healthProfile || {};
      const nextProfile = typeof updater === 'function'
        ? updater(currentProfile)
        : { ...currentProfile, ...(updater || {}) };

      setStore((current) => {
        const existing = current.subjects[subjectId];
        if (!existing) return current;
        const next = {
          ...current,
          subjects: {
            ...current.subjects,
            [subjectId]: {
              ...existing,
              healthProfile: nextProfile,
              updatedAt: new Date().toISOString()
            }
          }
        };
        persistStorage(next);
        return next;
      });

      logAudit({
        action: 'health_profile_updated',
        targetType: 'health-profile',
        targetId: subjectId
      });

      if (backendAvailable) {
        try {
          await saveHealthProfile(subjectId, {
            ownerUserId: user?.user_id || subjectId,
            actorId: user?.user_id || null,
            actorEmail: user?.email || null,
            profile: nextProfile
          });
        } catch {}
      }
    };

    const addConsent = async (payload) => {
      const consent = {
        id: crypto.randomUUID(),
        status: 'active',
        createdAt: new Date().toISOString(),
        beneficiaryId: activeSubject?.id || user?.user_id,
        beneficiaryName: activeSubject?.displayName || '',
        ...payload
      };

      setStore((current) => ({
        ...current,
        consents: [consent, ...current.consents]
      }));

      logAudit({
        action: 'consent_created',
        targetType: 'consent',
        targetId: consent.id,
        detail: `${consent.delegateName || consent.delegateEmail} authorized`
      });

      if (backendAvailable) {
        try {
          await createConsent(activeSubject?.id || user?.user_id, {
            ...payload,
            actorId: user?.user_id || null,
            actorEmail: user?.email || null
          });
        } catch {}
      }
    };

    const revokeConsent = async (consentId) => {
      setStore((current) => ({
        ...current,
        consents: current.consents.map((entry) => (
          entry.id === consentId
            ? { ...entry, status: 'revoked', revokedAt: new Date().toISOString() }
            : entry
        ))
      }));

      logAudit({
        action: 'consent_revoked',
        targetType: 'consent',
        targetId: consentId
      });

      if (backendAvailable) {
        try {
          await persistConsentRevoke(consentId, {
            subjectId: activeSubject?.id || null,
            actorId: user?.user_id || null,
            actorEmail: user?.email || null
          });
        } catch {}
      }
    };

    const addObservation = async (payload) => {
      const observation = {
        id: crypto.randomUUID(),
        createdAt: new Date().toISOString(),
        subjectId: payload.subjectId || activeSubject?.id || user?.user_id,
        authorId: payload.authorId || user?.user_id || null,
        providerId: payload.providerId || (user?.user_type === 'professional' ? user.user_id : null),
        status: 'open',
        ...payload
      };

      setStore((current) => ({
        ...current,
        observations: [observation, ...current.observations]
      }));

      logAudit({
        action: 'observation_added',
        targetType: 'observation',
        targetId: observation.id,
        detail: observation.noteType || observation.category || 'observation'
      });

      if (backendAvailable) {
        try {
          await createObservation(observation.subjectId, {
            ...observation,
            actorEmail: user?.email || null
          });
        } catch {}
      }

      return observation;
    };

    const addFeedback = async (payload) => {
      const feedback = {
        id: crypto.randomUUID(),
        createdAt: new Date().toISOString(),
        subjectId: activeSubject?.id || user?.user_id,
        authorId: user?.user_id || null,
        ...payload
      };

      setStore((current) => ({
        ...current,
        feedback: [feedback, ...current.feedback]
      }));

      logAudit({
        action: 'feedback_submitted',
        targetType: 'feedback',
        targetId: feedback.id
      });

      if (backendAvailable) {
        try {
          await createFeedback(feedback.subjectId, {
            ...feedback,
            actorEmail: user?.email || null
          });
        } catch {}
      }
    };

    const recordRecommendationAction = async (recommendation, action) => {
      setStore((current) => ({
        ...current,
        recommendationActions: [
          {
            id: crypto.randomUUID(),
            recommendationId: recommendation.id,
            action,
            actedAt: new Date().toISOString(),
            actorId: user?.user_id || null,
            subjectId: activeSubject?.id || null
          },
          ...current.recommendationActions
        ]
      }));

      logAudit({
        action: `recommendation_${action}`,
        targetType: 'recommendation',
        targetId: recommendation.id,
        detail: recommendation.title
      });

      if (backendAvailable) {
        try {
          await persistRecommendationAction(recommendation.id, {
            subjectId: activeSubject?.id || null,
            actorId: user?.user_id || null,
            actorEmail: user?.email || null,
            action,
            title: recommendation.title,
            metadata: {
              source: recommendation.source || null,
              sourceType: recommendation.sourceType || null
            }
          });
        } catch {}
      }
    };

    return {
      store,
      activeSubject,
      activeSubjectId,
      managedSubjects,
      setActiveSubjectId,
      recommendations,
      experience: deriveExperience(user, managedSubjects, activeSubject),
      healthProfileCompleteness: computeHealthProfileCompleteness(activeSubject?.healthProfile || {}),
      observations: store.observations.filter((entry) => entry.subjectId === activeSubject?.id),
      consents: store.consents.filter((entry) => entry.beneficiaryId === activeSubject?.id),
      feedback: store.feedback.filter((entry) => entry.subjectId === activeSubject?.id),
      auditLog: store.auditLog.filter((entry) => entry.subjectId === activeSubject?.id).slice(0, 12),
      updateHealthProfile,
      addConsent,
      revokeConsent,
      addObservation,
      addFeedback,
      recordRecommendationAction,
      backendAvailable
    };
  }, [activeSubject, activeSubjectId, backendAvailable, managedSubjects, recommendations, store, user]);

  return <CareCoordinatorContext.Provider value={value}>{children}</CareCoordinatorContext.Provider>;
}

export function useCareCoordinator() {
  return useContext(CareCoordinatorContext);
}
